import { useApi } from "./api";
import { useCallback } from "react";

const { v4: uuidv4 } = require("uuid");

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

// Utilidades (sin cambios)
const compressData = (data) => {
  try {
    return JSON.stringify(data).replace(/\s+/g, " ");
  } catch (error) {
    console.error("Error comprimiendo datos:", error);
    return data;
  }
};

const calculateObjectSize = (obj) => {
  const str = JSON.stringify(obj);
  return str.length * 2;
};

function isValidDateFormat(dateString) {
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormatRegex.test(dateString)) {
    return false;
  }
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
}

// ========= SATELLITE SERVICE =========
export const useSatelliteService = () => {
  const api = useApi();

  return {
    saveProcessedData: async (processedData, onProgress) => {
      // ... (mantener toda la lógica existente sin cambios)
      const delay = async (ms, message) => {
        console.log(message);
        await new Promise((resolve) => setTimeout(resolve, ms));
      };

      const calculateProgress = (
        currentChunk,
        totalChunks,
        chunkProgress = 100
      ) => {
        const chunkWeight = 100 / totalChunks;
        const totalProgress =
          (currentChunk - 1) * chunkWeight +
          chunkWeight * (chunkProgress / 100);
        return {
          percentage: Math.min(Math.round(totalProgress), 100),
          currentChunk: currentChunk,
          totalChunks: totalChunks,
          status: "processing",
        };
      };

      const createOptimalChunks = (vehicles) => {
        const chunks = [];
        let currentChunk = {};
        let currentSize = 0;
        const MAX_CHUNK_SIZE = 1 * 1024 * 1024;

        Object.entries(vehicles).forEach(([key, value]) => {
          const itemSize = calculateObjectSize({ [key]: value });

          if (currentSize + itemSize > MAX_CHUNK_SIZE) {
            if (Object.keys(currentChunk).length > 0) {
              chunks.push(currentChunk);
            }
            currentChunk = { [key]: value };
            currentSize = itemSize;
          } else {
            currentChunk[key] = value;
            currentSize += itemSize;
          }
        });

        if (Object.keys(currentChunk).length > 0) {
          chunks.push(currentChunk);
        }

        return chunks;
      };

      try {
        const compressedData = {
          ...processedData,
          vehicles: processedData.vehicles || {},
        };

        const sessionId = uuidv4();
        const vehicleChunks = createOptimalChunks(compressedData.vehicles);
        const totalChunks = vehicleChunks.length;
        const results = [];

        onProgress({
          percentage: 0,
          currentChunk: 0,
          totalChunks,
          sessionId,
          status: "processing",
          message: "Iniciando procesamiento de datos",
        });

        for (let i = 0; i < totalChunks; i++) {
          const chunk = {
            ...compressedData,
            vehicles: vehicleChunks[i],
            chunkInfo: {
              current: i + 1,
              total: totalChunks,
              vehiclesInChunk: Object.keys(vehicleChunks[i]).length,
              approximateSize: `${(
                calculateObjectSize(vehicleChunks[i]) / 1024
              ).toFixed(2)}KB`,
            },
          };

          let retryCount = 0;
          let success = false;

          while (retryCount < MAX_RETRIES && !success) {
            try {
              if (retryCount > 0) {
                const retryMessage = `Reintentando chunk ${
                  i + 1
                }/${totalChunks} (intento ${retryCount + 1}/${MAX_RETRIES})`;
                await delay(RETRY_DELAY * retryCount, retryMessage);
                onProgress({
                  ...calculateProgress(i + 1, totalChunks),
                  message: retryMessage,
                });
              }

              const response = await api.post(
                "/save-processed-data",
                compressData(chunk),
                {
                  headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-Chunk-Number": `${i + 1}`,
                    "X-Total-Chunks": `${totalChunks}`,
                    "X-Save-Session-Id": sessionId,
                  },
                  timeout: 60000,
                  maxContentLength: 2 * 1024 * 1024,
                  maxBodyLength: 2 * 1024 * 1024,
                  onUploadProgress: (progressEvent) => {
                    const chunkProgress = Math.round(
                      (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onProgress({
                      ...calculateProgress(i + 1, totalChunks, chunkProgress),
                      message: `Subiendo chunk ${
                        i + 1
                      }/${totalChunks}: ${chunkProgress}%`,
                    });
                  },
                }
              );

              success = true;
              results.push({
                chunk: i + 1,
                success: true,
                size: chunk.chunkInfo.approximateSize,
                data: response.data,
              });

              onProgress({
                ...calculateProgress(i + 1, totalChunks),
                message: `Chunk ${
                  i + 1
                }/${totalChunks} procesado correctamente`,
              });

              if (i < totalChunks - 1) {
                await delay(1000, "Esperando antes del siguiente chunk...");
              }
            } catch (error) {
              retryCount++;
              console.error(
                `❌ Error en chunk ${
                  i + 1
                }/${totalChunks} (intento ${retryCount}):`,
                error.message
              );

              if (error.response?.status === 413) {
                const errorMessage = `Chunk demasiado grande (${chunk.chunkInfo.approximateSize}). Considerando reducir CHUNK_SIZE.`;
                console.error(errorMessage);
                onProgress({
                  ...calculateProgress(i + 1, totalChunks),
                  status: "error",
                  message: errorMessage,
                });
              }

              if (retryCount === MAX_RETRIES) {
                try {
                  await api.post("/rollback-save-session", null, {
                    headers: { "X-Save-Session-Id": sessionId },
                  });
                  console.log(
                    `Rollback explícito solicitado para la sesión ${sessionId}`
                  );
                } catch (rollbackError) {
                  console.error("Error al solicitar rollback:", rollbackError);
                }
                results.push({
                  chunk: i + 1,
                  success: false,
                  size: chunk.chunkInfo.approximateSize,
                  error: error.response?.data || error.message,
                });

                throw {
                  message: `Error en el chunk ${
                    i + 1
                  }/${totalChunks} después de ${MAX_RETRIES} intentos`,
                  error: error.response?.data || error.message,
                  results,
                  failedChunkSize: chunk.chunkInfo.approximateSize,
                };
              }
            }
          }
        }

        onProgress({
          percentage: 100,
          currentChunk: totalChunks,
          totalChunks,
          status: "complete",
          message: "Proceso completado exitosamente",
        });

        return {
          success: true,
          totalChunks,
          totalProcessedSize: results.reduce((acc, curr) => {
            return acc + parseFloat(curr.size);
          }, 0),
          results,
        };
      } catch (error) {
        console.error("Error en el proceso de guardado:", error);
        onProgress({
          percentage: 0,
          status: "error",
          message: `Error: ${error.message}`,
        });
        throw error;
      }
    },

    getMonthlyVehicleAnalytics: async (date) => {
      try {
        if (!date || !isValidDateFormat(date)) {
          throw new Error("Formato de fecha inválido. Use YYYY-MM-DD");
        }
        const [year, month] = date.split("-");
        const response = await api.get("/monthly-vehicle-analytics", {
          params: { month, year },
        });
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo analíticas mensuales de vehículos:",
          error
        );
        throw error.response?.data || error.message;
      }
    },

    getDailyVehicleAnalytics: async (date) => {
      try {
        if (!date || !isValidDateFormat(date)) {
          throw new Error("Formato de fecha inválido. Use YYYY-MM-DD");
        }
        const [year, month, day] = date.split("-");
        const response = await api.get("/daily-vehicle-analytics", {
          params: { day, month, year },
        });
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo analíticas diarias de vehículos:",
          error
        );
        throw error.response?.data || error.message;
      }
    },

    uploadExcelFile: async (formData) => {
      try {
        const response = await api.post("/convert-excel-to-json", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
      } catch (error) {
        throw error.response?.data || error.message;
      }
    },

    getAnnualVehicleAnalytics: async (date) => {
      try {
        if (!date || !isValidDateFormat(date)) {
          throw new Error("Formato de fecha inválido. Use YYYY-MM-DD");
        }
        const [year, month] = date.split("-");
        const response = await api.get("/annual-vehicle-analytics", {
          params: { month, year },
        });
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo analíticas mensuales de vehículos:",
          error
        );
        throw error.response?.data || error.message;
      }
    },

    getAnnualHotspots: async (date) => {
      try {
        if (!date || !isValidDateFormat(date)) {
          throw new Error("Formato de fecha inválido. Use YYYY-MM-DD");
        }
        const [year, month] = date.split("-");
        const response = await api.get("/critical-point-violations-annual", {
          params: { month, year },
        });
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo analíticas mensuales de vehículos:",
          error
        );
        throw error.response?.data || error.message;
      }
    },
  };
};

// ========= USER SERVICE (OPERACIONES AUTENTICADAS) =========
export const useUserService = () => {
  const api = useApi();

  return {
    updateUser: async (userId, userData) => {
      try {
        const response = await api.put(`/user/${userId}`, userData);
        if (!response.data.success) {
          throw new Error(
            response.data.message || "Error al actualizar usuario"
          );
        }
        return response.data;
      } catch (error) {
        if (error.response) {
          throw {
            message:
              error.response.data.message || "Error al actualizar usuario",
            status: error.response.status,
            details: error.response.data,
          };
        } else if (error.request) {
          throw {
            message: "Error de conexión con el servidor",
            status: 503,
            details: error.request,
          };
        } else {
          throw {
            message: error.message || "Error desconocido",
            status: 500,
            details: error,
          };
        }
      }
    },

    getRoleWithUsers: async (selectedPage, limit) => {
      try {
        const response = await api.get(`/roles-with-users/${selectedPage}`, {
          params: { limit },
        });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo los roles con sus usuarios:", error);
        throw error.response?.data || error.message;
      }
    },

    getUsersByRole: async (roleId, page, limit, search) => {
      if (!roleId) throw new Error("Rol no proporcionado");
      const response = await api.get(`/${roleId}/users`, {
        params: {
          selectedPage: page,
          usersPerPage: limit,
          searchTerm: search,
        },
      });
      return response.data;
    },

    getUser: async (id) => {
      if (!id) throw new Error("Usuario no proporcionado");
      const response = await api.get(`/user/${id}`);
      return response.data;
    },

    getUsersWithRol: async (
      selectedPage,
      usersPerPage,
      searchTerm,
      role,
      filterIds = []
    ) => {
      try {
        const params = {
          usersPerPage,
          selectedPage,
          searchTerm,
        };

        if (filterIds && filterIds.length > 0) {
          params.filters = filterIds.join(",");
        }

        const response = await api.get(`/${role}/users`, { params });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo los usuarios con rol:", error);
        throw error.response?.data || error.message;
      }
    },

    getFiltersByRoleId: async (roleId) => {
      try {
        const response = await api.get(`/filters/role/${roleId}`);
        return response.data;
      } catch (error) {
        console.error("Error obteniendo filtros por rol:", error);
        throw error.response?.data || error.message;
      }
    },

    assignFilterToUser: async (userId, filterId) => {
      try {
        const response = await api.post("/filters/user", { userId, filterId });
        return response.data;
      } catch (error) {
        console.error("Error asignando filtro a usuario:", error);
        throw error.response?.data || error.message;
      }
    },

    removeFilterFromUser: async (userId, filterId) => {
      try {
        const response = await api.delete(
          `/filters/user/${userId}/${filterId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error removiendo filtro de usuario:", error);
        throw error.response?.data || error.message;
      }
    },

    getUsersByRoleWithoutPagination: async (roleId) => {
      try {
        const response = await api.get(`/${roleId}/users/all`);
        return response.data;
      } catch (error) {
        console.error("Error obteniendo todos los usuarios del rol:", error);
        throw error.response?.data || error.message;
      }
    },
  };
};

// ========= MEDIA SERVICE =========
export const useMediaService = () => {
  const api = useApi();

  return {
    getMediaWithUrl: async (url, type) => {
      try {
        const response = await api.get(`/media-document-stream-url/${url}`, {
          params: { type },
        });
        return response;
      } catch (error) {
        console.error("Error obteniendo los roles con sus usuarios:", error);
        throw error.response?.data || error.message;
      }
    },

    getVehicleMediaWithUrl: async (vehicleId, type) => {
      try {
        const response = await api.get(`/vehicle-media-stream/${vehicleId}`, {
          params: { type },
        });
        return response;
      } catch (error) {
        console.error("Error obteniendo media del vehículo:", error);
        throw error.response?.data || error.message;
      }
    },

    getMediaByFileId: async (fileId, type = "user") => {
      try {
        const response = await api.get(`/media-document-stream/${fileId}`, {
          params: { type },
        });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo el archivo por fileId:", error);
        throw error.response?.data || error.message;
      }
    },

    downloadDocument: async (fileId) => {
      try {
        const response = await api.get(`/download-document/${fileId}`, {
          responseType: "blob",
        });
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `document_${fileId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al descargar documento:", error);
        throw error;
      }
    },

    downloadMultipleDocuments: async (fileIds, typeData, signal) => {
      try {
        const response = await api.post(
          "/download-multiple-documents",
          { fileIds, typeData },
          {
            responseType: "blob",
            signal,
            headers: { Accept: "application/pdf" },
            onDownloadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        if (!(response.data instanceof Blob)) {
          throw new Error("Respuesta inválida del servidor");
        }

        const blob = new Blob([response.data], { type: "application/pdf" });

        if (blob.size === 0) {
          throw new Error("PDF generado está vacío");
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `documentos_${Date.now()}.pdf`);

        setTimeout(() => {
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }, 100);

        return { success: true };
      } catch (error) {
        if (api.isCancel && api.isCancel(error)) {
          console.log("Solicitud cancelada");
          throw new Error("Descarga cancelada por el usuario");
        }
        console.error("Error en la descarga:", error);
        throw error;
      }
    },

    updateDocument: async (documentId, documentData) => {
      try {
        const processFileToBase64 = async (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        };

        const prepareUpdatePayload = async (data) => {
          const payload = {
            documentTypeId:
              data.document_type?.id || data.document_type_id || data.docType,
            expeditionDate: data.expedition_date || null,
            expirationDate: data.expiration_date || null,
            originalNameDescription:
              data.description || data.original_name_description || "",
            required: Boolean(data.required),
            userId: data.userId,
          };

          // CRÍTICO: Manejo correcto del archivo y nombres
          if (data.file && data.file instanceof File) {
            const base64Content = await processFileToBase64(data.file);
            payload.content = base64Content;
            payload.fileName = data.file.name; // Nombre del archivo nuevo
            payload.originalName = data.file.name; // Nombre original del archivo nuevo
            payload.fileType = data.file.type;
            payload.fileSize = data.file.size;
          }

          return payload;
        };

        const payload = await prepareUpdatePayload(documentData);
        const response = await api.put(
          `/update-document/${documentId}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error en updateDocument:", error);
        throw error.response?.data || error;
      }
    },
    createDocument: async (documentData) => {
      try {
        const processFileToBase64 = async (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        };

        const prepareDocumentPayload = async (doc) => {
          if (!doc.file || !(doc.file instanceof File)) {
            throw new Error("Archivo inválido o no seleccionado");
          }

          const base64Content = await processFileToBase64(doc.file);

          return {
            documentTypeId: doc.documentTypeId,
            fileName: doc.fileName,
            originalName: doc.file.name,
            fileType: doc.file.type,
            fileSize: doc.file.size,
            content: base64Content,
            expeditionDate: doc.expeditionDate || null,
            expirationDate: doc.expirationDate || null,
            originalNameDescription: doc.description || "",
            required: Boolean(doc.required),
            userId: doc.userId,
          };
        };

        const payload = await prepareDocumentPayload(documentData);
        const response = await api.post("/create-documents", payload);
        return response.data;
      } catch (error) {
        console.error("Error en createDocument:", error);
        throw error.response?.data || error;
      }
    },

    deleteDocument: async (fileId) => {
      try {
        const response = await api.delete(`/delete-document/${fileId}`);
        return response.data;
      } catch (error) {
        console.error("Error al eliminar documento:", error);
        throw error;
      }
    },
  };
};

// ========= ROL SERVICE =========
export const useRolService = () => {
  const api = useApi();

  return {
    getRolService: async () => {
      try {
        const response = await api.get("/role");
        return response.data;
      } catch (error) {
        console.error("Error obteniendo los roles con sus usuarios:", error);
        throw error.response?.data || error.message;
      }
    },
  };
};

// ========= FILTER SERVICE =========
export const useFilterService = () => {
  const api = useApi();

  return {
    getAllFilters: async () => {
      try {
        const response = await api.get("/filters");
        return response.data;
      } catch (error) {
        console.error("Error obteniendo los filtros:", error);
        throw error.response?.data || error.message;
      }
    },

    getFiltersByRoleId: async (roleId) => {
      try {
        const response = await api.get(`/filters/role/${roleId}`);
        return response.data;
      } catch (error) {
        console.error("Error obteniendo filtros por rol:", error);
        throw error.response?.data || error.message;
      }
    },

    createFilter: async (filterData) => {
      try {
        const response = await api.post("/filters", filterData);
        return response.data;
      } catch (error) {
        console.error("Error creando filtro:", error);
        throw error.response?.data || error.message;
      }
    },

    createFilterRelationship: async (parentId, childId) => {
      try {
        const response = await api.post("/filters/relationship", {
          parentId,
          childId,
        });
        return response.data;
      } catch (error) {
        console.error("Error creando relación de filtros:", error);
        throw error.response?.data || error.message;
      }
    },

    assignFilterToRole: async (roleId, filterId) => {
      try {
        const response = await api.post("/filters/role", { roleId, filterId });
        return response.data;
      } catch (error) {
        console.error("Error asignando filtro a rol:", error);
        throw error.response?.data || error.message;
      }
    },

    removeFilterFromRole: async (roleId, filterId) => {
      try {
        const response = await api.delete(
          `/filters/role/${roleId}/${filterId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error removiendo filtro de rol:", error);
        throw error.response?.data || error.message;
      }
    },

    assignFilterToUser: async (userId, filterId) => {
      try {
        const response = await api.post("/filters/user", { userId, filterId });
        return response.data;
      } catch (error) {
        console.error("Error asignando filtro a usuario:", error);
        throw error.response?.data || error.message;
      }
    },

    removeFilterFromUser: async (userId, filterId) => {
      try {
        const response = await api.delete(
          `/filters/user/${userId}/${filterId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error removiendo filtro de usuario:", error);
        throw error.response?.data || error.message;
      }
    },

    updateFilter: async (filterId, filterData) => {
      try {
        const response = await api.put(`/filters/${filterId}`, filterData);
        return response.data;
      } catch (error) {
        console.error("Error actualizando filtro:", error);
        throw error.response?.data || error.message;
      }
    },

    deleteFilter: async (filterId) => {
      try {
        const response = await api.delete(`/filters/${filterId}`);
        return response.data;
      } catch (error) {
        console.error("Error eliminando filtro:", error);
        throw error.response?.data || error.message;
      }
    },

    removeFilterRelationship: async (parentId, childId) => {
      try {
        const response = await api.delete(
          `/filters/relationship/${parentId}/${childId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error eliminando relación de filtros:", error);
        throw error.response?.data || error.message;
      }
    },

    updateFilterPosition: async (filterId, position, targetCategory) => {
      try {
        const response = await api.put(`/filters/${filterId}/position`, {
          position,
          targetCategory,
        });
        return response.data;
      } catch (error) {
        console.error("Error actualizando posición:", error);
        throw error.response?.data || error.message;
      }
    },

    changeFilterCategory: async (filterId, category, position = 0) => {
      try {
        const response = await api.put(`/filters/${filterId}/category`, {
          category,
          position,
        });
        return response.data;
      } catch (error) {
        console.error("Error cambiando categoría:", error);
        throw error.response?.data || error.message;
      }
    },
  };
};
